import { AttachFile } from "@mui/icons-material";
import { Box, Button, ButtonBase, TextField } from "@mui/material";
import { useToast } from "contexts/ToastContext";
import { useRef } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

import FilePreview from "./FilePreview";

type ChatInputProps = {
  message: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSend: () => void;
  onFileSelect?: (file: File) => void;
  selectedFiles?: File[];
  onRemoveFile?: (index: number) => void;
};

const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "application/pdf",
  "image/gif",
];

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const ChatInput: React.FC<ChatInputProps> = ({
  message,
  onChange,
  onSend,
  onFileSelect,
  selectedFiles = [],
  onRemoveFile,
}: ChatInputProps) => {
  const { colors } = useCelleraTheme();
  const { showError } = useToast();

  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const allowedTypesString = ALLOWED_FILE_TYPES.map((type) => {
      if (type.startsWith("image/")) return type.split("/")[1].toUpperCase();
      if (type === "application/pdf") return "PDF";
      return type;
    }).join(", ");

    // Process all selected files
    Array.from(files).forEach((file) => {
      // Validate file type
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        showError(
          "Invalid file type",
          `Only ${allowedTypesString} files are allowed.`
        );
        return;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        showError(
          "File too large",
          `File size exceeds the maximum limit of 10MB. Please choose a smaller file.`
        );
        return;
      }

      onFileSelect?.(file);
    });

    inputRef.current?.focus();
    event.target.value = "";
  };

  return (
    <Box
      sx={{
        backgroundColor: colors.background.paper,
        position: "relative",
        pr: 1,
      }}
    >
      {/* File Previews */}
      {selectedFiles.length > 0 && (
        <Box
          sx={{
            display: "flex",
            maxHeight: "200px",
            overflowY: "auto",
            position: "absolute",
            bottom: "calc(100% - 10px)",
            left: 0,
            width: "100%",
            zIndex: 1000,
          }}
        >
          {selectedFiles.map((file, index) => (
            <FilePreview
              key={`${file.name}-${index}`}
              file={file}
              onRemove={() => onRemoveFile?.(index)}
            />
          ))}
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
        className="h-20"
      >
        <TextField
          inputRef={inputRef}
          placeholder="Type something"
          fullWidth
          variant="outlined"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              height: "100%",
              "& fieldset": {
                borderColor: colors.border.light,
              },
              "&:hover fieldset": {
                borderColor: colors.border.main,
              },
              "&.Mui-focused fieldset": {
                borderColor: colors.secondary.main,
              },
            },
          }}
          value={message}
          onChange={onChange}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSend();
            }
          }}
        />
        <ButtonBase
          component="label"
          tabIndex={-1}
          sx={{
            borderRadius: "40px",
            "&:has(:focus-visible)": {
              outline: "2px solid",
              outlineOffset: "2px",
            },
          }}
        >
          <AttachFile />
          <input
            type="file"
            accept={ALLOWED_FILE_TYPES.join(",")}
            onChange={handleFileChange}
            multiple
            style={{
              border: 0,
              clip: "rect(0 0 0 0)",
              height: "1px",
              margin: "-1px",
              overflow: "hidden",
              padding: 0,
              position: "absolute",
              whiteSpace: "nowrap",
              width: "1px",
            }}
          />
        </ButtonBase>

        <Button
          variant="contained"
          className="rounded-full px-6 py-2 font-inter text-white transition-all duration-200 hover:shadow-lg"
          sx={{
            backgroundColor: colors.primary.main,
            "&:hover": {
              backgroundColor: colors.primary.dark,
            },
          }}
          onClick={onSend}
        >
          Sent
        </Button>
      </Box>
    </Box>
  );
};

export default ChatInput;
